import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { X, Search, Loader2, Target, MapPin, Hash, Crosshair } from 'lucide-react';
import { geoSearch } from '../lib/geocode';
import { parseCoords } from './AddressSearch';
import { OLC_RE, olcDecode, olcRecover, olcIsFull, olcEncode } from '../lib/olc';

const KIND_ICON = { coord: Crosshair, olc: Hash, addr: MapPin };

// resolve input: coordinates | plus code (full / short + locality) | address
async function resolve(q, lang, center) {
  const c = parseCoords(q);
  if (c) return [{ ...c, kind: 'coord', name: `${c.lat.toFixed(7)}, ${c.lon.toFixed(7)}` }];
  const m = OLC_RE.exec(q.trim());
  if (m) {
    const code = m[1].toUpperCase();
    if (olcIsFull(code)) return [{ ...olcDecode(code), kind: 'olc', name: code }];
    let ref = { lat: center.lat, lon: center.lng }, where = '';
    if (m[2]) { const h = (await geoSearch(m[2], lang))[0]; if (h) { ref = h; where = ` · ${h.name.split(',').slice(0, 2).join(',')}`; } }
    return [{ ...olcRecover(code, ref.lat, ref.lon), kind: 'olc', name: `${code}${where}` }];
  }
  return (await geoSearch(q, lang)).map((h) => ({ ...h, kind: 'addr' }));
}

function Hit({ h, i, on, pick }) {
  const I = KIND_ICON[h.kind];
  return (
    <li><button type="button" className={`ts-hit ${on ? 'on' : ''}`} data-testid={`target-hit-${i}`} onClick={pick}>
      <I size={15} /><span><b>{h.name}</b><small>{h.lat.toFixed(6)}, {h.lon.toFixed(6)} · {olcEncode(h.lat, h.lon)}</small></span>
    </button></li>
  );
}

// universal target search (address / coordinates / plus code) as a clean dialog
export default function TargetSearch({ t, mapRef, onClose }) {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [hits, setHits] = useState(null);
  const [sel, setSel] = useState(0);
  const inRef = useRef(null);
  useEffect(() => { inRef.current?.focus(); const k = (e) => e.key === 'Escape' && onClose(); window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k); }, [onClose]);
  const lang = (t.locale || 'de').slice(0, 2);
  const search = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setBusy(true);
    try { const r = await resolve(q, lang, mapRef.current.getCenter()); setHits(r); setSel(0); if (!r.length) toast.error(t.tsNone); } catch { toast.error(t.tsOffline); }
    setBusy(false);
  };
  const jump = () => { const h = hits?.[sel]; if (!h) return; mapRef.current.targetMark(h); onClose(); };
  return createPortal(
    <div className="editor-backdrop ts-backdrop" data-testid="target-search-dialog" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="editor ts-box">
        <div className="row pf-head"><h4><Target size={18} /> {t.tsTitle}</h4><button className="icon-btn" data-testid="target-search-close-btn" onClick={onClose}><X size={18} /></button></div>
        <form className="ts-form" onSubmit={search}>
          <input ref={inRef} data-testid="target-search-input" value={q} placeholder={t.tsPh} onChange={(e) => { setQ(e.target.value); setHits(null); }} />
          <button className="btn btn-dark" data-testid="target-search-submit" disabled={busy}>{busy ? <Loader2 size={16} className="spin" /> : <Search size={16} />}</button>
        </form>
        <small className="muted">{t.tsHint}</small>
        {hits?.length > 0 && <ul className="ts-hits" data-testid="target-hits">{hits.map((h, i) => <Hit key={`${h.lat}-${h.lon}-${i}`} h={h} i={i} on={i === sel} pick={() => setSel(i)} />)}</ul>}
        <button className="btn btn-green ts-jump" data-testid="target-jump-btn" disabled={!hits?.length} onClick={jump}><Target size={16} /> {t.tsJump}</button>
      </div>
    </div>,
    document.body,
  );
}
